// src/components/investigations/InvestigationForm.js
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { getErrorMessage } from '../../utils/error';

const InvestigationForm = ({ onClose, onSuccess, investigation = null }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: investigation?.title || '',
    description: investigation?.description || '',
    priority: investigation?.priority || 'medium',
    tags: investigation?.tags?.join(', ') || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.title.trim()) {
      setError(t('journalist.investigations.validation.titleRequired'));
      return;
    }

    setLoading(true);
    try {
      const tagsArray = formData.tags
        ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0)
        : [];

      if (investigation) {
        // Update existing investigation
        await api.patch(`/api/journalist/investigations/${investigation.id}`, {
          title: formData.title,
          description: formData.description,
          priority: formData.priority,
          tags: tagsArray,
        });
      } else {
        // Create new investigation
        await api.post('/api/journalist/investigations', {
          title: formData.title,
          description: formData.description,
          priority: formData.priority,
          tags: tagsArray,
        });
      }

      onSuccess && onSuccess();
    } catch (err) {
      setError(getErrorMessage(err, t('journalist.investigations.error.saveFailed')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-shayek-light-300 dark:border-shayek-dark-200">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 dark:text-shayek-dark-900">
              {investigation ? t('journalist.investigations.edit') : t('journalist.investigations.new')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400 whitespace-pre-line">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-shayek-dark-700 mb-2">
              {t('journalist.investigations.form.title')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-shayek-dark-200 rounded-lg focus:ring-2 focus:ring-shayek-primary focus:border-transparent bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900"
              placeholder={t('journalist.investigations.form.titlePlaceholder')}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-shayek-dark-700 mb-2">
              {t('journalist.investigations.form.description')}
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 dark:border-shayek-dark-200 rounded-lg focus:ring-2 focus:ring-shayek-primary focus:border-transparent resize-none bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900"
              placeholder={t('journalist.investigations.form.descriptionPlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-shayek-dark-700 mb-2">
              {t('journalist.investigations.form.priority')}
            </label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-shayek-dark-200 rounded-lg focus:ring-2 focus:ring-shayek-primary focus:border-transparent bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900"
            >
              <option value="low">{t('journalist.investigations.priority.low')}</option>
              <option value="medium">{t('journalist.investigations.priority.medium')}</option>
              <option value="high">{t('journalist.investigations.priority.high')}</option>
              <option value="urgent">{t('journalist.investigations.priority.urgent')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-shayek-dark-700 mb-2">
              {t('journalist.investigations.form.tags')}
            </label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-shayek-dark-200 rounded-lg focus:ring-2 focus:ring-shayek-primary focus:border-transparent bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900"
              placeholder={t('journalist.investigations.form.tagsPlaceholder')}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('journalist.investigations.form.tagsHint')}</p>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-shayek-primary text-white rounded-lg font-medium hover:bg-shayek-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? t('common.loading') : (investigation ? t('common.save') : t('journalist.investigations.create'))}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              {t('common.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvestigationForm;

